import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, FileText, Layers } from "lucide-react";
import {
  useMyEntryReviewsQuery,
  useDecideEntryReviewMutation,
} from "../api/committeeEntryReviewApi";
import LoadError from "../components/LoadError";
import { ScanPreview, SubjectGrades, certOf } from "../components/evidence";

const dash = (v: any) => (v === null || v === undefined || v === "" ? "—" : String(v));

function DecisionBadge({ decision }: { decision: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${
        map[decision] || map.pending
      }`}
    >
      {decision}
    </span>
  );
}

function Field({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 break-words">{dash(value)}</div>
    </div>
  );
}

function Section({
  title,
  rows,
  columns,
}: {
  title: string;
  rows: any[];
  columns: { label: string; get: (r: any) => any }[];
}) {
  if (!rows?.length) return null;
  return (
    <div>
      <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">{title}</h4>
      <div className="border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-gray-400 border-b">
              {columns.map((c) => (
                <th key={c.label} className="px-3 py-2">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r: any, i: number) => (
              <tr key={r.id ?? i}>
                {columns.map((c) => (
                  <td key={c.label} className="px-3 py-2 text-gray-700">
                    {dash(c.get(r))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Academic qualifications, with the certificate on the page.
 *
 * Same treatment as the O'level sittings below and for the same reason: the
 * certificate IS the qualification as far as a reviewer is concerned, and a
 * "View" link in a table cell is a decision nobody takes — they read the row
 * and move on, which is how an unsupported degree gets recommended.
 */
function Qualifications({ rows }: { rows: any[] }) {
  if (!rows?.length) return null;

  return (
    <div>
      <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
        Qualifications
      </h4>

      <div className="space-y-3">
        {rows.map((r: any, i: number) => (
          <div key={r.id ?? i} className="border rounded-xl p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1 min-w-[16rem]">
                <Field label="Institution" value={r.institution} />
                <Field label="Qualification" value={r.qualification} />
                <Field label="Year" value={r.year} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                  Certificate
                </div>
                <ScanPreview path={certOf(r)} alt="Certificate" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * O'level sittings, out of the table the rest of this screen uses.
 *
 * A sitting is not a row of four cells: it carries up to nine subjects and the
 * scanned slip they were read off, and both of those want room. The slip is
 * shown rather than linked because checking the grades against it is the whole
 * of what a reviewer does here.
 */
function OLevelResults({ rows }: { rows: any[] }) {
  if (!rows?.length) return null;

  return (
    <div>
      <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
        O'Level results
      </h4>

      <div className="space-y-3">
        {rows.map((r: any, i: number) => (
          <div key={r.id ?? i} className="border rounded-xl p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1 min-w-[16rem]">
                <Field label="Exam" value={String(r.exam_type ?? "").toUpperCase()} />
                <Field label="Year" value={r.exam_year} />
                <Field label="Exam no." value={r.exam_number} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                  Scanned result
                </div>
                <ScanPreview path={r.result_file} alt="Scanned result" />
              </div>
            </div>

            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
                Subjects
              </div>
              <SubjectGrades subjects={r.subjects} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The application itself. A recommendation cannot be made from a name and a
 * category, so everything the applicant submitted is here.
 */
export function EntryApplicationDetails({ app }: { app: any }) {
  const refName = (r: any) =>
    r.member
      ? [r.member.surname, r.member.firstname, r.member.othernames].filter(Boolean).join(" ")
      : "—";

  return (
    <div className="space-y-6 pt-4 border-t">
      <div>
        <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Applicant</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            label="Name"
            value={[app.title, app.surname, app.other_names].filter(Boolean).join(" ")}
          />
          <Field label="Gender" value={app.gender} />
          <Field label="Date of birth" value={app.date_of_birth} />
          <Field label="Nationality" value={app.nationality} />
          <Field label="State" value={app.state?.name} />
          <Field label="LGA" value={app.lga?.name} />
          <Field label="Chapter" value={app.chapter?.name ?? app.chapter_other} />
          <Field label="Sector" value={app.sector?.name} />
          <Field label="Email" value={app.email ?? app.user?.email} />
          <Field label="Phone" value={app.phone} />
          <Field label="Residential address" value={app.residential_address} />
          <Field label="Postal address" value={app.postal_address} />
        </div>
      </div>

      <div>
        <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
          Application
        </h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Category" value={app.category?.name} />
          <Field label="Status" value={app.status} />
          <Field label="Diet" value={app.diet?.title} />
          <Field label="Chapter endorsed" value={app.chapter_accepted_at} />
        </div>
      </div>

      <Qualifications rows={app.qualifications ?? []} />

      <OLevelResults rows={app.o_level_results ?? app.olevel_results ?? []} />

      <Section
        title="Professional memberships"
        rows={app.professional_memberships ?? []}
        columns={[
          { label: "Body", get: (r) => r.body_name },
          { label: "Stage passed", get: (r) => r.stage_passed },
        ]}
      />

      <Section
        title="Work experience"
        rows={app.work_experiences ?? []}
        columns={[
          { label: "Organisation", get: (r) => r.organization },
          { label: "Position", get: (r) => r.position },
          { label: "From", get: (r) => r.start_date },
          { label: "To", get: (r) => r.end_date ?? "Present" },
          { label: "Responsibilities", get: (r) => r.responsibilities },
        ]}
      />

      <Section
        title="Seminars & workshops"
        rows={app.seminars ?? []}
        columns={[
          { label: "Title", get: (r) => r.title },
          { label: "Date", get: (r) => r.date },
          { label: "Location", get: (r) => r.location },
        ]}
      />

      <Section
        title="Referees"
        rows={app.referees ?? []}
        columns={[
          { label: "Referee", get: refName },
          { label: "Membership no.", get: (r) => r.member?.membership_no },
          { label: "Relationship", get: (r) => r.relationship },
          { label: "Response", get: (r) => r.decision },
          { label: "Remarks", get: (r) => r.remarks },
        ]}
      />

      <Section
        title="Present employer's comments"
        rows={app.present_employer_comments ?? []}
        columns={[
          { label: "Employer", get: (r) => r.employer_name ?? r.organization },
          { label: "Comment", get: (r) => r.comment ?? r.comments },
        ]}
      />
    </div>
  );
}

function ReviewCard({ row }: { row: any }) {
  const app = row.application || {};
  const [decision, setDecision] = useState<"approved" | "rejected" | "">(
    row.decision === "pending" ? "" : row.decision,
  );
  const [remark, setRemark] = useState<string>(row.remark ?? "");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [decide, { isLoading }] = useDecideEntryReviewMutation();

  const submit = async () => {
    setBanner(null);
    if (!decision) {
      setBanner({ ok: false, text: "Select approve or reject." });
      return;
    }
    try {
      await decide({ id: app.id, decision, remark: remark.trim() || undefined }).unwrap();
      setBanner({ ok: true, text: "Your recommendation has been recorded." });
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || e?.message || "Could not save your recommendation.",
      });
    }
  };

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <div className="p-5 border-b bg-indigo-50/40 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-gray-900">
            {[app.surname, app.other_names].filter(Boolean).join(" ") || app.user?.name || "Applicant"}
          </div>
          <div className="text-sm text-gray-500">{app.category?.name ?? "Entry application"}</div>
          <div className="text-xs text-gray-400 mt-0.5">{app.email ?? app.user?.email}</div>
          {row.diet ? (
            <div className="text-[11px] font-semibold text-indigo-600 mt-1">
              Diet: {row.diet.title}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500">Your recommendation:</span>
          <DecisionBadge decision={row.decision} />
        </div>
      </div>

      <div className="p-5 space-y-4">
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          <FileText className="w-4 h-4" />
          {open ? "Hide application" : "View application"}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {open ? <EntryApplicationDetails app={app} /> : null}

        {banner ? (
          <div
            className={`text-sm rounded-lg p-3 border ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                : "bg-red-50 border-red-100 text-red-700"
            }`}
          >
            {banner.text}
          </div>
        ) : null}

        <div className="flex gap-2 pt-2 border-t">
          <button
            onClick={() => setDecision("approved")}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-semibold border ${
              decision === "approved"
                ? "bg-emerald-600 text-white border-emerald-600"
                : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            }`}
          >
            Recommend approval
          </button>
          <button
            onClick={() => setDecision("rejected")}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-semibold border ${
              decision === "rejected"
                ? "bg-red-600 text-white border-red-600"
                : "border-red-200 text-red-600 hover:bg-red-50"
            }`}
          >
            Recommend rejection
          </button>
        </div>

        <textarea
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          rows={3}
          placeholder="Your remark / reason (optional)"
          className="w-full rounded-lg border px-3 py-2 text-sm"
        />

        <div className="flex justify-end">
          <button
            onClick={submit}
            disabled={isLoading || !decision}
            className="px-5 py-2.5 rounded-lg bg-gradient-purple text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {isLoading
              ? "Saving…"
              : row.decision === "pending"
              ? "Submit recommendation"
              : "Update recommendation"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Diets ───────────────────────────────────────────────────────────────
   A committee member's assignments are not one list, they are one list PER
   SITTING: the diet is what the chairman finalises against and what everyone
   talks about ("the March diet"), so it is the top level here and the
   recommendation states are sections inside it. */

const DIET_STATUS_STYLE: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
};

/** Assignments with no diet still have to be reachable, so they get their own group. */
const NO_DIET = "__none__";

type Bucket = "pending" | "approved" | "rejected";

/** Pending first: it is the only one of the three that is work. */
const BUCKETS: { key: Bucket; label: string; tone: string; active: string; empty: string }[] = [
  {
    key: "pending",
    label: "Pending review",
    tone: "bg-amber-50 text-amber-800 border-amber-200",
    active: "bg-amber-600 text-white border-amber-600",
    empty: "Nothing is waiting on your recommendation in this diet.",
  },
  {
    key: "approved",
    label: "Approved",
    tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    active: "bg-emerald-600 text-white border-emerald-600",
    empty: "You have not recommended any application in this diet for approval.",
  },
  {
    key: "rejected",
    label: "Rejected",
    tone: "bg-red-50 text-red-800 border-red-200",
    active: "bg-red-600 text-white border-red-600",
    empty: "You have not recommended any application in this diet for rejection.",
  },
];

type Diet = {
  key: string;
  title: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  buckets: Record<Bucket, any[]>;
  total: number;
};

/** My assignments, grouped into the diets that produced them. */
function groupByDiet(rows: any[]): Diet[] {
  const map = new Map<string, Diet>();

  for (const r of rows) {
    const diet = r.diet ?? r.application?.diet;
    const key = diet?.id ? String(diet.id) : NO_DIET;

    if (!map.has(key)) {
      map.set(key, {
        key,
        title: diet?.title ?? "Not in a diet",
        status: diet?.status,
        start_date: diet?.start_date,
        end_date: diet?.end_date,
        buckets: { pending: [], approved: [], rejected: [] },
        total: 0,
      });
    }

    const group = map.get(key)!;
    // Anything the server has not recognised as a recommendation reads as
    // pending — an assignment that fell out of all three sections would be
    // invisible, which is worse than being in the wrong one.
    const d = String(r.decision ?? "pending");
    group.buckets[(d === "approved" || d === "rejected" ? d : "pending") as Bucket].push(r);
    group.total += 1;
  }

  // Array.from, not a spread: the project's tsconfig target predates iterating
  // a Map without --downlevelIteration.
  return Array.from(map.values()).sort((a, b) => {
    if (a.key === NO_DIET) return 1;
    if (b.key === NO_DIET) return -1;
    const pending = b.buckets.pending.length - a.buckets.pending.length;
    if (pending !== 0) return pending;
    return a.title.localeCompare(b.title);
  });
}

function Pill({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${tone}`}
    >
      <b className="tabular-nums">{value}</b> {label}
    </span>
  );
}

function DietGroup({
  diet,
  open,
  onToggle,
}: {
  diet: Diet;
  open: boolean;
  onToggle: () => void;
}) {
  /**
   * Which of the three is being read. Opens on whichever one is the work —
   * pending unless there is none left, and then the whole diet is history and
   * what was approved is the more useful first page.
   */
  const [section, setSection] = useState<Bucket>(
    diet.buckets.pending.length ? "pending" : "approved",
  );

  const active = BUCKETS.find((b) => b.key === section) ?? BUCKETS[0];
  const shown = diet.buckets[section];

  return (
    <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left p-5 hover:bg-gray-50 transition flex items-start justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <Layers className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{diet.title}</span>
              {diet.status ? (
                <span
                  className={`inline-flex text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                    DIET_STATUS_STYLE[diet.status] ?? DIET_STATUS_STYLE.pending
                  }`}
                >
                  {diet.status}
                </span>
              ) : null}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {diet.key === NO_DIET
                ? "Assigned outside a diet"
                : `${diet.start_date || "—"} → ${diet.end_date || "—"}`}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Pill label="assigned" value={diet.total} tone="bg-gray-50 text-gray-600 border-gray-200" />
              {BUCKETS.map((b) => (
                <Pill
                  key={b.key}
                  label={b.key === "pending" ? "pending" : b.key}
                  value={diet.buckets[b.key].length}
                  tone={
                    diet.buckets[b.key].length
                      ? b.tone
                      : "bg-gray-50 text-gray-400 border-gray-200"
                  }
                />
              ))}
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-5 h-5 text-gray-300 mt-2 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-300 mt-2 shrink-0" />
        )}
      </button>

      {open ? (
        <div className="border-t bg-gray-50/60 p-4 md:p-5 space-y-4">
          {/* One section at a time. All three are always listed with their
              counts, so nothing is hidden — but only the chosen one is read,
              because a diet of forty applications stacked three deep is a
              scroll, not a screen. */}
          <div className="flex flex-wrap gap-2">
            {BUCKETS.map((b) => {
              const count = diet.buckets[b.key].length;
              const current = section === b.key;

              return (
                <button
                  key={b.key}
                  onClick={() => setSection(b.key)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                    current ? b.active : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {b.label} <span className="tabular-nums opacity-70">({count})</span>
                </button>
              );
            })}
          </div>

          {/* An empty section says so rather than showing nothing: the three
              are the shape of the screen, and silence reads as a loading
              fault rather than as "none yet". */}
          {shown.length === 0 ? (
            <p className="text-sm text-gray-400 bg-white border rounded-xl px-4 py-3">{active.empty}</p>
          ) : (
            <div className="space-y-3">
              {shown.map((r: any) => (
                <ReviewCard key={r.application?.id} row={r} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function MyEntryReviewsPage() {
  const { data: rows = [], isLoading, isError, error, refetch } = useMyEntryReviewsQuery();

  const diets = useMemo(() => groupByDiet(rows), [rows]);

  /**
   * Which diet is open. `null` means "nothing has been clicked yet", which is
   * not the same as "everything is closed" — the effect below opens the one
   * carrying the work, and a member who then collapses it gets "" and keeps it
   * collapsed rather than having it spring open again on the next render.
   */
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    if (openKey !== null || diets.length === 0) return;

    const withWork = diets.find((d) => d.buckets.pending.length > 0);
    setOpenKey((withWork ?? diets[0]).key);
  }, [diets, openKey]);

  const pending = rows.filter((r: any) => String(r.decision ?? "pending") === "pending").length;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <div className="rounded-2xl bg-gradient-purple text-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold">My Entry Application Reviews</h1>
        <p className="text-sm text-white/80 mt-1">
          Entry applications assigned to you by the secretariat, grouped by diet. Open a diet, read
          each application, and record your recommendation. The committee chairman finalises.
        </p>
        {rows.length ? (
          <p className="text-sm text-white/90 font-semibold mt-2">
            {pending} of {rows.length} still awaiting your recommendation · {diets.length} diet
            {diets.length === 1 ? "" : "s"}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <div className="text-gray-600">Loading…</div>
      ) : isError ? (
        <LoadError error={error} onRetry={refetch} />
      ) : !rows.length ? (
        <div className="bg-white border rounded-2xl p-8 text-center text-gray-500">
          No entry applications are assigned to you right now.
        </div>
      ) : (
        <div className="space-y-3">
          {diets.map((d) => (
            <DietGroup
              key={d.key}
              diet={d}
              open={openKey === d.key}
              onToggle={() => setOpenKey(openKey === d.key ? "" : d.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
